/* ============================================================
   reveal.js — lightweight scroll-reveal (replaces GSAP + ScrollTrigger)
   Adds `.is-revealed` to elements as they enter the viewport; CSS does the
   fade/lift/scale via transitions. ~1KB, no dependencies.

   Markers:
     [data-reveal]          — fade + lift once on enter
     [data-reveal-stagger]  — children fade + lift, staggered
     [data-blog-bg]         — one-time scale-in (was the GSAP parallax)

   Safety:
   - The <head> arming script adds `reveal-on` before paint (skipped under
     reduced-motion) and sets window.__revealFallback to un-arm after 3s if
     this file never runs. We clear that timer on init.
   - No IntersectionObserver support → reveal everything immediately.
   ============================================================ */
(function () {
  'use strict';
  var d = document.documentElement;
  var SEL = '[data-reveal], [data-reveal-stagger], [data-blog-bg]';

  // We're taking over — cancel the "un-arm if JS never loads" failsafe.
  if (window.__revealFallback) { clearTimeout(window.__revealFallback); window.__revealFallback = null; }

  // Public API for code that injects markup after load (plp.js filter/sort swap
  // and Load More). Defined BEFORE the early returns so call sites can invoke it
  // unconditionally: when reveal isn't armed these stay no-ops and the CSS has
  // already left everything visible.
  var api = (window.GrittReveal = { refresh: function () {} });

  // Not armed (reduced-motion, or splash) → nothing to do; content is already visible.
  if (!d.classList.contains('reveal-on')) return;

  // No IO support → just reveal everything (drop the hidden state).
  if (!('IntersectionObserver' in window)) { d.classList.remove('reveal-on'); return; }

  // Stagger groups that have already revealed their children. The group is
  // unobserved at that moment, so anything appended into it afterwards would
  // never be reached again — refresh() uses this to tell the two cases apart.
  var fired = new WeakSet();

  function stagger(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].style.transitionDelay = (i * 0.08) + 's';
      nodes[i].classList.add('is-revealed');
    }
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      if (el.hasAttribute('data-reveal-stagger')) {
        stagger(el.children);
        fired.add(el);
      } else {
        el.classList.add('is-revealed');
      }
      io.unobserve(el);
    });
  }, { rootMargin: '0px', threshold: 0 });

  /** Observe every marker inside `root` (and root itself). Safe to call twice —
      observing an already-observed element is a no-op. */
  function observeIn(root) {
    if (!root) return;
    if (root.nodeType === 1 && root.matches && root.matches(SEL)) io.observe(root);
    var found = root.querySelectorAll(SEL);
    for (var i = 0; i < found.length; i++) io.observe(found[i]);
  }

  /** Tell reveal.js about DOM that appeared after load — a container whose
      contents you replaced, or the list of nodes you appended. ONE method on
      purpose: which of those two it is decides between "observe it" and "reveal
      it right now", and that depends on whether the enclosing stagger group has
      already fired — an observer detail the caller has no business knowing.
      Guessing wrong renders nothing and throws no error, which is the exact bug
      this API exists to fix. */
  api.refresh = function (target) {
    if (!target) return;

    // An element: its markers are new DOM the observer has never seen.
    if (target.nodeType === 1) { observeIn(target); return; }

    // A list of appended nodes: a node that is itself a marker gets observed;
    // otherwise it belongs to a stagger group, and only a group that already
    // fired leaves it stranded (a group still being watched will reveal it).
    var stranded = [];
    for (var i = 0; i < target.length; i++) {
      var node = target[i];
      if (!node || node.nodeType !== 1) continue;
      if (node.matches(SEL)) { io.observe(node); continue; }
      var group = node.closest('[data-reveal-stagger]');
      if (group && fired.has(group)) stranded.push(node);
    }
    stagger(stranded);
  };

  observeIn(document);
})();
